import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SmsLegalShell from "./SmsLegalShell";

/**
 * Standalone SMS opt-in form for Life Foundation · The Exchange Alliance.
 * Public URL: /sms/subscribe
 *
 * ⚠️ TWILIO TFV / TCR COMPLIANCE — this page is the dedicated, single-
 * purpose SMS consent form referenced in /sms/terms § 2. It exists
 * specifically to give Twilio reviewers a public, isolated URL where
 * they can verify:
 *
 *   1. SMS consent is collected through an EXPLICIT checkbox that is
 *      UNCHECKED by default (no pre-checked boxes, no implicit consent).
 *   2. The consent decision is OPTIONAL — the form makes it abundantly
 *      clear in plain language that opting in is voluntary and that
 *      every Life Foundation / Exchange Alliance service is available
 *      to people who never opt in.
 *   3. The consent language references program identity, message
 *      categories, frequency, "Msg & data rates may apply", STOP/HELP
 *      instructions, and links to the SMS Terms + Privacy Policy.
 *   4. The form is NOT bundled with any other agreement (Terms of
 *      Service, Privacy Policy acceptance, intake paperwork, lease,
 *      employment). It is the SOLE purpose of this URL.
 *
 * Removing or modifying any of these guarantees will cause Twilio to
 * reject the Toll-Free Verification with reason 30505 ("Agreeing to
 * Receive Messages Must Be Optional").
 */
export default function SmsSubscribe() {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api/site`;

  const [form, setForm] = useState({
    full_name:    "",
    phone:        "",
    relationship: "",
    consent:      false,   // ⚠️ DO NOT pre-check this — Twilio rule.
    hp:           "",      // honeypot
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) =>
    setForm((p) => ({
      ...p,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name.trim() || form.full_name.trim().length < 2) {
      setError("Please enter your full name.");
      return;
    }
    const digits = (form.phone || "").replace(/\D/g, "");
    if (digits.length < 10) {
      setError("Please enter a valid 10-digit U.S. mobile number.");
      return;
    }
    if (!form.consent) {
      setError("To subscribe to SMS, please check the consent box. (You can use every Life Foundation service without subscribing.)");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/sms/opt-in`, {
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        relationship: form.relationship || "",
        consent: true,
        consent_text_version: "v1",
        hp: form.hp || "",
      }, { withCredentials: false });
      if (res?.data?.ok) {
        setSubmitted(true);
      } else {
        setError("Submission did not complete. Please try again.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Submission failed. Please try again or call (843) 424-2655.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <SmsLegalShell
        testId="sms-subscribe-success"
        eyebrow="SMS Program · Subscription Confirmed"
        title="You're Subscribed"
        lastUpdated={null}
      >
        <p>
          Thank you, <strong>{form.full_name}</strong>. You have successfully
          opted in to receive case-management text messages from{" "}
          <strong>Life Foundation · The Exchange Alliance</strong> at the
          mobile number ending in{" "}
          <strong>{(form.phone.replace(/\D/g, "").slice(-4)) || "----"}</strong>.
        </p>
        <p>
          <strong>What happens next:</strong> You may receive a confirmation
          text shortly. From there, message frequency varies based on your
          role and active cases (typically 1–8 per month).
        </p>
        <h2>You are always in control</h2>
        <ul>
          <li>Reply <strong>STOP</strong> to any message to unsubscribe.</li>
          <li>Reply <strong>HELP</strong> to any message for support.</li>
          <li>Message and data rates may apply.</li>
          <li>
            Need help right away? Email{" "}
            <a href="mailto:will.smith@lifefoundationrebuild.com">
              will.smith@lifefoundationrebuild.com
            </a>{" "}
            or call <a href="tel:+18434242655">+1 (843) 424-2655</a>.
          </li>
        </ul>
        <p>
          For the full program details, see our{" "}
          <Link to="/sms/terms">SMS Terms &amp; Conditions</Link> and{" "}
          <Link to="/sms/privacy">SMS Privacy Policy</Link>.
        </p>
      </SmsLegalShell>
    );
  }

  return (
    <SmsLegalShell
      testId="sms-subscribe-page"
      eyebrow="SMS Program · Voluntary Opt-In"
      title="Subscribe to SMS Updates"
      lastUpdated={null}
    >
      {/* Plain-English statement of voluntariness — this banner exists
          specifically so a Twilio reviewer can see at-a-glance that
          consent is OPTIONAL and never a condition of service. */}
      <div
        data-testid="sms-subscribe-voluntary-banner"
        style={{
          border: "1px solid rgba(212,175,55,0.4)",
          background: "rgba(212,175,55,0.06)",
          padding: "14px 16px",
          borderRadius: 6,
          marginBottom: 24,
        }}
      >
        <p style={{ margin: 0 }}>
          <strong>This form is optional.</strong> Subscribing to SMS is{" "}
          <strong>never required</strong> to receive housing, case management,
          employment placement, life-skills training, financial assistance,
          or any other service from Life Foundation / The Exchange Alliance.
          You can close this page and continue with any program without
          providing a phone number.
        </p>
      </div>

      <p>
        If you would like the convenience of text-message reminders and
        case-management updates, you can opt in below. We honor STOP
        immediately, and we never share your number with marketers.
      </p>

      <form onSubmit={submit} data-testid="sms-subscribe-form" noValidate>
        {/* Honeypot — invisible to humans, bots fill it and we drop them */}
        <div aria-hidden="true" style={{ position: "absolute", left: -9999, height: 0, overflow: "hidden" }}>
          <label htmlFor="hp-field">Leave this empty</label>
          <input
            id="hp-field"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.hp}
            onChange={set("hp")}
          />
        </div>

        <div style={{ display: "grid", gap: 18, marginTop: 18 }}>
          {/* Full name */}
          <div>
            <label
              htmlFor="sms-fullname"
              style={{ display: "block", fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: "#a1a1aa", marginBottom: 6 }}
            >
              Full name <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              id="sms-fullname"
              data-testid="sms-subscribe-fullname"
              type="text"
              autoComplete="name"
              required
              maxLength={160}
              value={form.full_name}
              onChange={set("full_name")}
              style={{
                width: "100%",
                background: "#0f1620",
                border: "1px solid #27272a",
                borderRadius: 4,
                padding: "10px 12px",
                color: "#f4f4f5",
                fontSize: 15,
                outline: "none",
              }}
            />
          </div>

          {/* Mobile phone */}
          <div>
            <label
              htmlFor="sms-phone"
              style={{ display: "block", fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: "#a1a1aa", marginBottom: 6 }}
            >
              Mobile phone number (U.S.) <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              id="sms-phone"
              data-testid="sms-subscribe-phone"
              type="tel"
              autoComplete="tel"
              required
              inputMode="tel"
              placeholder="(555) 555-5555"
              maxLength={40}
              value={form.phone}
              onChange={set("phone")}
              style={{
                width: "100%",
                background: "#0f1620",
                border: "1px solid #27272a",
                borderRadius: 4,
                padding: "10px 12px",
                color: "#f4f4f5",
                fontSize: 15,
                outline: "none",
              }}
            />
            <p style={{ fontSize: 12, color: "#71717a", marginTop: 6, marginBottom: 0 }}>
              Standard mobile number only. Landlines cannot receive SMS.
            </p>
          </div>

          {/* Relationship (optional context) */}
          <div>
            <label
              htmlFor="sms-relationship"
              style={{ display: "block", fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: "#a1a1aa", marginBottom: 6 }}
            >
              I am a&hellip; (optional)
            </label>
            <select
              id="sms-relationship"
              data-testid="sms-subscribe-relationship"
              value={form.relationship}
              onChange={set("relationship")}
              style={{
                width: "100%",
                background: "#0f1620",
                border: "1px solid #27272a",
                borderRadius: 4,
                padding: "10px 12px",
                color: "#f4f4f5",
                fontSize: 15,
                outline: "none",
              }}
            >
              <option value="">— Select one (optional) —</option>
              <option value="resident">Resident / Program Participant</option>
              <option value="staff">Staff Member</option>
              <option value="volunteer">Volunteer</option>
              <option value="partner">Community / Agency Partner</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Consent checkbox — UNCHECKED by default. The Twilio rule
              that triggered 30505 hinges on this checkbox being
              explicitly unchecked at page-load. NEVER pre-check it. */}
          <div
            style={{
              border: "1px solid #3f3f46",
              borderRadius: 6,
              padding: "14px 16px",
              background: "#0f1620",
            }}
          >
            <label
              htmlFor="sms-consent"
              data-testid="sms-subscribe-consent-label"
              style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }}
            >
              <input
                id="sms-consent"
                data-testid="sms-subscribe-consent-checkbox"
                type="checkbox"
                checked={form.consent}
                onChange={set("consent")}
                style={{ marginTop: 4, transform: "scale(1.15)" }}
              />
              <span style={{ fontSize: 14, lineHeight: 1.55, color: "#e4e4e7" }}>
                <strong>I expressly consent</strong> to receive text messages
                from <strong>Life Foundation · The Exchange Alliance</strong>{" "}
                about case management, appointment reminders, document
                requests, housing application updates, and operational
                alerts at the mobile number I provided. Message frequency
                varies (typically 1–8 per month).{" "}
                <strong>Message and data rates may apply.</strong> I can
                reply <strong>STOP</strong> at any time to unsubscribe and{" "}
                <strong>HELP</strong> for help. Consent is <em>not</em> a
                condition of receiving services from Life Foundation. I
                have read the{" "}
                <Link to="/sms/terms" style={{ color: "#d4af37" }}>
                  SMS Terms
                </Link>{" "}
                and{" "}
                <Link to="/sms/privacy" style={{ color: "#d4af37" }}>
                  Privacy Policy
                </Link>
                .
              </span>
            </label>
          </div>

          {/* Inline error */}
          {error && (
            <div
              data-testid="sms-subscribe-error"
              style={{
                color: "#fca5a5",
                background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.4)",
                padding: "10px 12px",
                borderRadius: 4,
                fontSize: 14,
              }}
            >
              {error}
            </div>
          )}

          {/* Submit + cancel */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 6 }}>
            <button
              type="submit"
              data-testid="sms-subscribe-submit"
              disabled={loading}
              style={{
                background: "#d4af37",
                color: "#0b0f15",
                border: "none",
                borderRadius: 4,
                padding: "12px 22px",
                fontSize: 13,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                fontWeight: 700,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? "Subscribing…" : "Subscribe to SMS Updates"}
            </button>

            <Link
              to="/"
              data-testid="sms-subscribe-cancel"
              style={{
                color: "#a1a1aa",
                fontSize: 13,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                padding: "12px 22px",
                border: "1px solid #3f3f46",
                borderRadius: 4,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              Cancel · I don&rsquo;t want SMS
            </Link>
          </div>

          <p style={{ fontSize: 12, color: "#71717a", marginTop: 0, marginBottom: 0 }}>
            By submitting, only your name, phone number, and the consent
            timestamp are stored. We never sell or share your number with
            marketers. See our{" "}
            <Link to="/sms/privacy" style={{ color: "#d4af37" }}>Privacy Policy</Link>.
          </p>
        </div>
      </form>
    </SmsLegalShell>
  );
}
